import type { OnlineClient, OnlineQueryValue } from './client.js';
import { OnlineApiError } from './errors.js';

export const DEFAULT_MAX_PAGES = 100;
export const MAX_ALLOWED_PAGES = 100;

export async function fetchOnlinePages(
  client: OnlineClient,
  path: string,
  query: Record<string, OnlineQueryValue>,
  allPages: boolean,
  maxPages = DEFAULT_MAX_PAGES,
): Promise<unknown> {
  if (!Number.isInteger(maxPages) || maxPages < 1 || maxPages > MAX_ALLOWED_PAGES) {
    throw new OnlineApiError(
      `--max-pages must be an integer between 1 and ${MAX_ALLOWED_PAGES}`,
      'invalid-request',
    );
  }
  if (!allPages) return (await client.get(path, { query })).data;

  const items: unknown[] = [];
  const cursors = new Set<string>();
  let cursor = typeof query.cursor === 'string' ? query.cursor : undefined;
  if (cursor) cursors.add(cursor);
  let metadata: Record<string, unknown> = {};

  for (let page = 1; page <= maxPages; page++) {
    const response = await client.get(path, { query: { ...query, cursor } });
    if (!response.data || typeof response.data !== 'object') {
      throw new OnlineApiError('Paginated User API response must be an object', 'invalid-response');
    }
    const data = response.data as Record<string, unknown>;
    if (!Array.isArray(data.items)) {
      throw new OnlineApiError(
        'Paginated User API response contains no items array',
        'invalid-response',
      );
    }
    items.push(...data.items);
    const { items: _items, next_cursor: nextCursor, ...rest } = data;
    metadata = { ...metadata, ...rest };

    if (nextCursor === undefined) return { ...metadata, items, pagesFetched: page };
    if (typeof nextCursor !== 'string' || !nextCursor) {
      throw new OnlineApiError(
        'User API returned an invalid pagination cursor',
        'invalid-response',
      );
    }
    if (cursors.has(nextCursor)) {
      throw new OnlineApiError('User API pagination cursor repeated', 'invalid-response');
    }
    cursors.add(nextCursor);
    cursor = nextCursor;
  }

  throw new OnlineApiError(`User API pagination exceeded ${maxPages} pages`, 'invalid-response');
}
